import React, { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, Loader2, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import CurrencyInput from '../ui/CurrencyInput';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { Production } from '../../types';
import { supabase } from '../../lib/supabase';
import { useMemo } from 'react';
import EntityModal from './EntityModal';
import ErrorModal from './ErrorModal';
import ConfirmModal from './ConfirmModal';
import { formatCurrency } from '../../utils/formatters';
import { getCurrentArgentinaISOString } from '../../utils/dateUtils';
import { logChangesAndNotify } from '../../utils/changeLog';
import ChangeHistoryModal from './ChangeHistoryModal';
import { useAuth } from '../../context/AuthContext';
import PaymentReceiptField from '../shared/PaymentReceiptField';
import { isPaymentReceiptValid, notifyPaymentReceiptUploaded } from '../../utils/paymentReceipt';

interface ProductionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (production: Omit<Production, 'id' | 'created_at' | 'updated_at'>) => void;
  initialData?: Production;
}

// Define localStorage keys
const STORAGE_KEYS = {
  APPROVED_FILE: 'production_approved_file',
  SERVICE_AGREEMENT_FILE: 'production_service_agreement_file',
  DTF_FILE: 'production_dtf_file',
  EMBROIDERY_FILE: 'production_embroidery_file',
  PAYMENT_RECEIPT: 'production_payment_receipt',
  INVOICE_FILE: 'production_invoice_file',
  FORM_DATA: 'production_form_data',
  FILE_NAMES: 'production_file_names'
};

const ProductionModal: React.FC<ProductionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const { clients, addClient, salespersons } = useAppStore();
  const { userRole } = useAuth();
  const hideFinancialFields = userRole === 'Producción';
  const canBypassRequiredFields = userRole === 'Producción' || userRole === 'Administrador' || userRole === 'Super Admin';
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    approved_file: '',
    service_agreement_file: '',
    dtf_file: '',
    embroidery_file: '',
    start_date: getCurrentArgentinaISOString(),
    delivery_date: '',
    salesperson: '' as const,
    payment_receipt: '',
    cash_payment: false,
    invoice_file: '',
    status: 'pending' as const,
    production_value: '0',
    comment: '',
  });

  const [isUploading, setIsUploading] = useState({
    approved: false,
    serviceAgreement: false,
    dtf: false,
    embroidery: false,
    receipt: false,
    invoice: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileToDelete, setFileToDelete] = useState<{field: string, fileName: string} | null>(null);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [formModified, setFormModified] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Historial de cambios
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [createdByEmail, setCreatedByEmail] = useState<string | null>(null);
  
  // Prepare salesperson options
  const salespersonOptions = useMemo(() => [
    { value: '', label: 'Seleccionar vendedor...' },
    ...salespersons.map(sp => ({ value: sp.name, label: `${sp.name} (${sp.email})` }))
  ], [salespersons]);

  const paymentReceiptCtx = () => ({
    hideFinancialFields,
    canBypassRequiredFields,
    userRole,
    cashPayment: Boolean(formData.cash_payment),
    paymentReceipt: formData.payment_receipt || '',
  });

  // Load data from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      try {
        /* ─── NUEVO ─── */
        if (!initialData) {
          // Es "Nueva Producción" → limpiar todo
          localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
          localStorage.removeItem(STORAGE_KEYS.SERVICE_AGREEMENT_FILE);
          localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
          localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
          localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
          localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
          localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
          localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);

          // Reiniciar campos del formulario
          setFormData({
            name: '',
            client_id: '',
            approved_file: '',
            service_agreement_file: '',
            dtf_file: '',
            embroidery_file: '',
            start_date: getCurrentArgentinaISOString(),
            delivery_date: '',
            salesperson: '',
            payment_receipt: '',
            cash_payment: false,
            invoice_file: '',
            status: 'pending',
            production_value: '0',
            comment: '',
          });

          setFileNames({});
          setFormModified(false);
          return;          // ← saltamos el resto del try{} (no cargamos nada viejo)
        }
        /* ───────────── */

        // Load files
        const approvedFile = localStorage.getItem(STORAGE_KEYS.APPROVED_FILE);
        const serviceAgreementFile = localStorage.getItem(STORAGE_KEYS.SERVICE_AGREEMENT_FILE);
        const dtfFile = localStorage.getItem(STORAGE_KEYS.DTF_FILE);
        const embroideryFile = localStorage.getItem(STORAGE_KEYS.EMBROIDERY_FILE);
        const paymentReceipt = localStorage.getItem(STORAGE_KEYS.PAYMENT_RECEIPT);
        const invoiceFile = localStorage.getItem(STORAGE_KEYS.INVOICE_FILE);
        
        // Load form data
        const savedFormData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
        const parsedFormData = savedFormData ? JSON.parse(savedFormData) : null;
        
        // Load file names
        const savedFileNames = localStorage.getItem(STORAGE_KEYS.FILE_NAMES);
        const parsedFileNames = savedFileNames ? JSON.parse(savedFileNames) : {};
        
        if (initialData) {
          // If editing an existing production
          setFormData({
            name: initialData.name,
            client_id: initialData.client_id,
            approved_file: initialData.approved_file || '',
            service_agreement_file: (initialData as any).service_agreement_file || '',
            dtf_file: initialData.dtf_file || '',
            embroidery_file: initialData.embroidery_file || '',
            start_date: (initialData as any).start_date || initialData.created_at?.slice(0, 10) || getCurrentArgentinaISOString(),
            delivery_date: initialData.delivery_date,
            salesperson: initialData.salesperson,
            payment_receipt: initialData.payment_receipt || '',
            cash_payment: Boolean((initialData as any).cash_payment),
            invoice_file: (initialData as any).invoice_file || '',
            status: initialData.status,
            production_value: initialData.production_value?.toString() || '0',
            comment: initialData.comment || '',
          });

          if (initialData.file_names) {
            setFileNames(JSON.parse(initialData.file_names));
          }
          
          // Check if we have saved changes for this production
          if (parsedFormData) {
            // Apply saved changes on top of the database data
            setFormData(prev => ({
              ...prev,
              ...(parsedFormData || {}),
              approved_file: approvedFile || prev.approved_file,
              service_agreement_file: serviceAgreementFile || prev.service_agreement_file,
              dtf_file: dtfFile || prev.dtf_file,
              embroidery_file: embroideryFile || prev.embroidery_file,
              payment_receipt: paymentReceipt || prev.payment_receipt,
              invoice_file: invoiceFile || prev.invoice_file,
            }));
            
            // Apply saved file names
            if (parsedFileNames) {
              setFileNames(parsedFileNames);
            }
            
            setFormModified(true);
          }
        } else {
          // If creating a new production
          // Update state with saved data
          setFormData(prev => ({
            ...prev,
            ...(parsedFormData || {}),
            approved_file: approvedFile || '',
            service_agreement_file: serviceAgreementFile || '',
            dtf_file: dtfFile || '',
            embroidery_file: embroideryFile || '',
            payment_receipt: paymentReceipt || '',
            invoice_file: invoiceFile || '',
          }));
          
          // Apply saved file names
          if (parsedFileNames) {
            setFileNames(parsedFileNames);
          }
        }

        // Reset form modified flag
        setFormModified(false);
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
        // If there's an error, just continue with empty form
      }
    }
  }, [isOpen, initialData]);

  // Obtener email del creador desde el primer registro del historial
  useEffect(() => {
    const fetchCreator = async () => {
      if (!initialData) return;
      const { data } = await supabase
        .from('entity_change_log')
        .select('changed_by_email, created_at')
        .eq('entity_type', 'production')
        .eq('entity_id', initialData.id)
        .order('created_at', { ascending: true })
        .limit(1);
      const emailFromLog = data?.[0]?.changed_by_email || null;
      if (emailFromLog) {
        setCreatedByEmail(emailFromLog);
      } else {
        // Fallback: buscar email del vendedor asignado
        const sp = salespersons.find(sp => sp.name === initialData.salesperson);
        setCreatedByEmail(sp?.email || null);
      }
    };
    fetchCreator();
  }, [initialData]);

  // Cierre con Escape y bloqueo de scroll cuando el historial está abierto
  useEffect(() => {
    if (!isHistoryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsHistoryOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isHistoryOpen]);

  // Save form data to localStorage when it changes
  useEffect(() => {
    if (isOpen && formModified) {
      try {
        // Save form data without the file URLs (we store those separately)
        const formDataToSave = {
          ...formData,
          approved_file: undefined,
          service_agreement_file: undefined,
          dtf_file: undefined,
          embroidery_file: undefined,
          payment_receipt: undefined,
          invoice_file: undefined,
        };
        
        localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formDataToSave));
        
        // Save file URLs separately
        if (formData.approved_file) {
          localStorage.setItem(STORAGE_KEYS.APPROVED_FILE, formData.approved_file);
        }
        
        if (formData.dtf_file) {
          localStorage.setItem(STORAGE_KEYS.DTF_FILE, formData.dtf_file);
        }
        
        if (formData.embroidery_file) {
          localStorage.setItem(STORAGE_KEYS.EMBROIDERY_FILE, formData.embroidery_file);
        }
        
        if (formData.payment_receipt) {
          localStorage.setItem(STORAGE_KEYS.PAYMENT_RECEIPT, formData.payment_receipt);
        }
        
        if (formData.invoice_file) {
          localStorage.setItem(STORAGE_KEYS.INVOICE_FILE, formData.invoice_file);
        }
        
        // Save file names
        localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify(fileNames));
      } catch (error) {
        console.error('Error saving data to localStorage:', error);
        // If there's an error saving, just continue
      }
    }
  }, [formData, isOpen, formModified, fileNames]);

  const handleFileUpload = async (files: FileList, type: 'approved' | 'serviceAgreement' | 'dtf' | 'embroidery' | 'receipt' | 'invoice') => {
    const uploadStateMap = {
      approved: 'approved_file',
      serviceAgreement: 'service_agreement_file',
      dtf: 'dtf_file',
      embroidery: 'embroidery_file',
      receipt: 'payment_receipt',
      invoice: 'invoice_file',
    };

    const storageKeyMap = {
      approved: STORAGE_KEYS.APPROVED_FILE,
      serviceAgreement: STORAGE_KEYS.SERVICE_AGREEMENT_FILE,
      dtf: STORAGE_KEYS.DTF_FILE,
      embroidery: STORAGE_KEYS.EMBROIDERY_FILE,
      receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
      invoice: STORAGE_KEYS.INVOICE_FILE,
    };

    // Validar tamaño de archivos (límite de 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB en bytes
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > maxSize) {
        setErrorMessage(`El archivo "${files[i].name}" es demasiado grande. El tamaño máximo permitido es 10MB.`);
        setErrorModalOpen(true);
        return;
      }
    }

    try {
      setIsUploading(prev => ({ ...prev, [type]: true }));

      const uploadedUrls: string[] = [];
      const uploadedFileNames: string[] = [];

      // Process each file in the FileList
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Usar el nombre ORIGINAL del archivo para el path en Storage
        const fileName = file.name;
        const filePath = `${type}/${fileName}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('production-files')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('production-files')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        uploadedFileNames.push(file.name);
      }

      // Save the original file names
      setFileNames(prev => ({
        ...prev,
        [uploadStateMap[type]]: uploadedFileNames
      }));

      // Join multiple URLs with a delimiter if there are multiple files
      const urlString = uploadedUrls.join('|');

      setFormData(prev => ({
        ...prev,
        [uploadStateMap[type]]: urlString,
      }));
      setFormModified(true);

      // Save to localStorage
      localStorage.setItem(storageKeyMap[type], urlString);
      
      // Save file names to localStorage
      localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
        ...fileNames,
        [uploadStateMap[type]]: uploadedFileNames
      }));

      if (type === 'receipt') {
        const clientName = clients.find((c) => c.id === formData.client_id)?.name || '—';
        await notifyPaymentReceiptUploaded({
          entityType: 'production',
          entityId: initialData?.id || null,
          entityName: formData.name || 'Producción',
          clientName,
          receiptUrl: urlString,
          salesperson: formData.salesperson,
        });
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      if (error.statusCode === '413' || error.message?.includes('Payload too large')) {
        setErrorMessage('Uno o más archivos son demasiado grandes. El tamaño máximo permitido es 10MB por archivo.');
      } else {
        setErrorMessage(error.message || 'Error al subir el archivo. Por favor, intente nuevamente.');
      }
      setErrorModalOpen(true);
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteFile = (field: string) => {
    // Extract file names from URL string
    const urls = (formData[field as keyof typeof formData] as string).split('|');
    const fileNamesArray = fileNames[field] || urls.map(url => url.split('/').pop() || 'archivo');
    
    setFileToDelete({ field, fileName: fileNamesArray.join(', ') });
    setIsDeleteFileModalOpen(true);
  };

  const confirmDeleteFile = () => {
    if (!fileToDelete) return;
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [fileToDelete.field]: '',
    }));
    
    // If we're creating a new production, also remove from localStorage
    if (!initialData) {
      const storageKeyMap: Record<string, string> = {
        approved_file: STORAGE_KEYS.APPROVED_FILE,
        service_agreement_file: STORAGE_KEYS.SERVICE_AGREEMENT_FILE,
        dtf_file: STORAGE_KEYS.DTF_FILE,
        embroidery_file: STORAGE_KEYS.EMBROIDERY_FILE,
        payment_receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
        invoice_file: STORAGE_KEYS.INVOICE_FILE,
      };
      
      const storageKey = storageKeyMap[fileToDelete.field];
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    }
    
    // Remove from fileNames
    setFileNames(prev => {
      const newFileNames = {...prev};
      delete newFileNames[fileToDelete.field];
      return newFileNames;
    });
    
    // Update fileNames in localStorage
    localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
      ...fileNames,
      [fileToDelete.field]: undefined
    }));
    
    setFileToDelete(null);
    setFormModified(true);
  };

  const getFileNameFromUrl = (url: string, field: string): string => {
    // First check if we have the original filenames stored
    if (fileNames[field] && fileNames[field].length > 0) {
      return fileNames[field].join(', ');
    }
    
    // Otherwise extract from URL
    const urls = url.split('|');
    return urls.map(u => {
      const parts = u.split('/');
      return parts[parts.length - 1] || 'archivo';
    }).join(', ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields (administradores pueden omitir)
    if (!canBypassRequiredFields) {
      if (!formData.approved_file) {
        setErrorMessage('La ficha aprobada es obligatoria');
        setErrorModalOpen(true);
        return;
      }
      if (!formData.invoice_file) {
        setErrorMessage('El archivo de factura es obligatorio');
        setErrorModalOpen(true);
        return;
      }
      if (!isPaymentReceiptValid(paymentReceiptCtx())) {
        setErrorMessage('El comprobante de pago es obligatorio');
        setErrorModalOpen(true);
        return;
      }
    }
    
    setIsSaving(true);
    const payload = {
      ...formData,
      production_value: parseFloat(formData.production_value),
      file_names: JSON.stringify(fileNames),
    } as any;

    // Si es edición, registrar cambios antes de guardar
    if (initialData) {
      await logChangesAndNotify({
        entityType: 'production',
        entityId: initialData.id,
        entityName: initialData.name,
        previous: initialData as any,
        next: { ...initialData, ...payload } as any,
        includeFields: ['name','client_id','delivery_date','status','salesperson','comment','approved_file','service_agreement_file','dtf_file','embroidery_file','payment_receipt','invoice_file']
      });
    }

    try {
      await onSave(payload);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewClient = async (client: any) => {
    try {
      const newClient = await addClient(client);
      setFormData(prev => ({ ...prev, client_id: newClient.id }));
      setIsClientModalOpen(false);
      setFormModified(true);
    } catch (error: any) {
      console.error('Error creating client:', error);
      setErrorMessage('Error al crear el cliente');
      setErrorModalOpen(true);
    }
  };

  const handleCloseClick = () => {
    if (formModified) {
      setIsConfirmCloseModalOpen(true);
    } else {
      onClose();
    }
  };

  // Clear localStorage and close modal
  const handleConfirmClose = () => {
    // Clear localStorage when modal is closed
    localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
    localStorage.removeItem(STORAGE_KEYS.SERVICE_AGREEMENT_FILE);
    localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
    localStorage.removeItem(STORAGE_KEYS.EMBROIDERY_FILE);
    localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
    localStorage.removeItem(STORAGE_KEYS.INVOICE_FILE);
    localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
    
    // Reset form to empty state
    setFormData({
      name: '',
      client_id: '',
      approved_file: '',
      service_agreement_file: '',
      dtf_file: '',
      embroidery_file: '',
      start_date: getCurrentArgentinaISOString(),
      delivery_date: '',
      salesperson: '',
      payment_receipt: '',
      invoice_file: '',
      status: 'pending',
      production_value: '0',
      comment: '',
    });
    
    // Reset other state
    setFileNames({});
    setFormModified(false);
    
    // Close confirm modal
    setIsConfirmCloseModalOpen(false);
    
    // Call the original onClose function
    onClose();
  };

  // Format production value as currency
  const formatProductionValueAsCurrency = (value: string) => {
    if (!value || isNaN(parseFloat(value))) return '';
    return formatCurrency(parseFloat(value));
  };

  // Render file links for multiple files
  const renderFileLinks = (urls: string, field: string) => {
    if (!urls) return null;
    
    const urlArray = urls.split('|');
    const fileNamesArray = fileNames[field] || urlArray.map(url => url.split('/').pop() || 'archivo');
    
    return (
      <div className="flex flex-col gap-1">
        {urlArray.map((url, index) => (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {fileNamesArray[index] || `Archivo ${index + 1}`}
          </a>
        ))}
      </div>
    );
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleCloseClick}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 max-h-[90vh] rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none flex flex-col min-h-0 z-50">
            {/* Header compacto estilo InvoiceModal */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {initialData ? 'Editar' : 'Nueva'} Producción
                  </h2>
                  {initialData && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Creado: {new Date(initialData.created_at).toLocaleString()} · {createdByEmail || 'usuario desconocido'}
                    </p>
                  )}
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600" aria-label="Cerrar" onClick={(e) => { e.preventDefault(); handleCloseClick(); }}>
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Nombre *</label>
                    <input
                      type="text"
                      placeholder="Ej: Buzos canguro"
                      value={formData.name}
                      onChange={(e) => { setFormData(prev => ({ ...prev, name: e.target.value })); setFormModified(true); }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Cliente *</label>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 min-w-0 [&>div]:!mb-0">
                        <Select
                          label=""
                          value={formData.client_id}
                          onChange={(value) => { setFormData(prev => ({ ...prev, client_id: value })); setFormModified(true); }}
                          options={clients.map(client => ({ value: client.id, label: client.name }))}
                          required
                        />
                      </div>
                      <Button type="button" variant="outline" onClick={() => setIsClientModalOpen(true)} icon={<Plus className="h-4 w-4" />} className="h-12 shrink-0 self-end">
                        Nuevo
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fecha de inicio *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => { setFormData(prev => ({ ...prev, start_date: e.target.value })); setFormModified(true); }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fecha de entrega *</label>
                    <input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => { setFormData(prev => ({ ...prev, delivery_date: e.target.value })); setFormModified(true); }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2 [&>div]:!mb-0">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Vendedor *</label>
                    <Select
                      label=""
                      value={formData.salesperson}
                      onChange={(value) => { setFormData(prev => ({ ...prev, salesperson: value })); setFormModified(true); }}
                      options={salespersonOptions}
                      required
                    />
                  </div>
                </div>

                {!hideFinancialFields && (
                <CurrencyInput
                  label="Valor de la producción"
                  value={formData.production_value}
                  onChange={(value) => { setFormData(prev => ({ ...prev, production_value: value })); setFormModified(true); }}
                  id="production_value"
                  name="production_value"
                  required
                  className="!mb-0"
                />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Ficha Aprobada *</label>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) { const dt = new DataTransfer(); dt.items.add(f[0]); handleFileUpload(dt.files, 'approved'); } }} className="hidden" id="approved-file" />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('approved-file')?.click()} disabled={isUploading.approved || !!formData.approved_file}
                        icon={isUploading.approved ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.approved_file ? 'Cambiar' : 'Subir'}
                      </Button>
                      {formData.approved_file && (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {renderFileLinks(formData.approved_file, 'approved_file')}
                          <button type="button" onClick={() => handleDeleteFile('approved_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    {!formData.approved_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  {!hideFinancialFields && (
                  <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Acuerdo de servicio firmado *</label>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) { const dt = new DataTransfer(); dt.items.add(f[0]); handleFileUpload(dt.files, 'serviceAgreement'); } }} className="hidden" id="service-agreement-file" />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('service-agreement-file')?.click()} disabled={isUploading.serviceAgreement || !!formData.service_agreement_file}
                        icon={isUploading.serviceAgreement ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.service_agreement_file ? 'Cambiar' : 'Subir'}
                      </Button>
                      {formData.service_agreement_file && (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {renderFileLinks(formData.service_agreement_file, 'service_agreement_file')}
                          <button type="button" onClick={() => handleDeleteFile('service_agreement_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    {!formData.service_agreement_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Factura *</label>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) { const dt = new DataTransfer(); dt.items.add(f[0]); handleFileUpload(dt.files, 'invoice'); } }} className="hidden" id="invoice-file" />
                      <Button type="button" variant="outline" onClick={() => document.getElementById('invoice-file')?.click()} disabled={isUploading.invoice || !!formData.invoice_file}
                        icon={isUploading.invoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm shrink-0">
                        {formData.invoice_file ? 'Cambiar' : 'Subir'}
                      </Button>
                      {formData.invoice_file && (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {renderFileLinks(formData.invoice_file, 'invoice_file')}
                          <button type="button" onClick={() => handleDeleteFile('invoice_file')} className="text-gray-400 hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      )}
                    </div>
                    {!formData.invoice_file && <p className="text-[11px] text-red-500 mt-0.5">Obligatorio</p>}
                  </div>
                  </>
                  )}
                </div>

                {/* Más opciones */}
                <div className="pt-2">
                  <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
                    {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    Más opciones
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-100 dark:border-gray-800">
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Archivo DTF</span>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'dtf'); }} className="hidden" id="dtf-file" multiple />
                          <Button type="button" variant="outline" onClick={() => document.getElementById('dtf-file')?.click()} disabled={isUploading.dtf}
                            icon={isUploading.dtf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir DTF</Button>
                          {formData.dtf_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.dtf_file, 'dtf_file')}</div><button type="button" onClick={() => handleDeleteFile('dtf_file')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Archivo Bordado</span>
                        <div className="flex items-center gap-2 mt-1">
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const f = e.target.files; if (f?.length) handleFileUpload(f, 'embroidery'); }} className="hidden" id="embroidery-file" multiple />
                          <Button type="button" variant="outline" onClick={() => document.getElementById('embroidery-file')?.click()} disabled={isUploading.embroidery}
                            icon={isUploading.embroidery ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} className="h-9 text-sm">Subir Bordado</Button>
                          {formData.embroidery_file && (<><div className="flex-1 min-w-0">{renderFileLinks(formData.embroidery_file, 'embroidery_file')}</div><button type="button" onClick={() => handleDeleteFile('embroidery_file')} className="text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></>)}
                        </div>
                      </div>
                      {!hideFinancialFields && (
                      <PaymentReceiptField
                        inputId="payment-receipt"
                        paymentReceipt={formData.payment_receipt}
                        cashPayment={Boolean(formData.cash_payment)}
                        userRole={userRole}
                        hideFinancialFields={hideFinancialFields}
                        canBypassRequiredFields={canBypassRequiredFields}
                        isUploading={isUploading.receipt}
                        onUpload={(files) => handleFileUpload(files, 'receipt')}
                        onDelete={() => handleDeleteFile('payment_receipt')}
                        onCashPaymentChange={(checked) => { setFormData((prev) => ({ ...prev, cash_payment: checked })); setFormModified(true); }}
                        renderFileLinks={renderFileLinks}
                      />
                      )}
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Comentarios</span>
                        <textarea
                          id="comment"
                          name="comment"
                          rows={2}
                          value={formData.comment}
                          onChange={(e) => { setFormData(prev => ({ ...prev, comment: e.target.value })); setFormModified(true); }}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400"
                          placeholder="Opcional..."
                        />
                      </div>
                      {initialData && (
                        <Select
                          label="Estado"
                          value={formData.status}
                          onChange={(value) => { setFormData(prev => ({ ...prev, status: value as Production['status'] })); setFormModified(true); }}
                          options={[
                            { value: 'pending', label: 'Pendiente' },
                            { value: 'in_progress', label: 'En Proceso' },
                            { value: 'finished', label: 'Terminado' },
                          ]}
                          required
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2 shrink-0">
                {initialData ? (
                  <button
                    type="button"
                    className="text-xs underline text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={async () => {
                      setIsHistoryOpen(true);
                      setIsLoadingHistory(true);
                      const { data } = await supabase
                        .from('entity_change_log')
                        .select('*')
                        .eq('entity_type', 'production')
                        .eq('entity_id', initialData.id)
                        .order('created_at', { ascending: false });
                      setHistoryItems(data || []);
                      setIsLoadingHistory(false);
                    }}
                  >
                    Historial de cambios
                  </button>
                ) : <span />}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseClick}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving || (!canBypassRequiredFields && (!formData.name || !formData.client_id || !formData.delivery_date || !formData.salesperson || !formData.approved_file || (!hideFinancialFields && (!formData.service_agreement_file || !formData.invoice_file || !isPaymentReceiptValid(paymentReceiptCtx())))))}
                >
                  {isSaving ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Guardando...
                    </span>
                  ) : (
                    initialData ? 'Guardar Cambios' : 'Crear Producción'
                  )}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ChangeHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        entityType="production"
        entityId={initialData?.id}
        createdAt={initialData?.created_at}
        createdByEmail={createdByEmail || undefined}
      />

      <EntityModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        entityType="client"
        onSave={handleNewClient}
      />

      <ErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        message={errorMessage}
      />

      <ConfirmModal
        isOpen={isDeleteFileModalOpen}
        onClose={() => {
          setIsDeleteFileModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={confirmDeleteFile}
        title="Eliminar archivo"
        message={`¿Estás seguro de que querés eliminar el archivo ${fileToDelete?.fileName}?`}
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={isConfirmCloseModalOpen}
        onClose={() => setIsConfirmCloseModalOpen(false)}
        onConfirm={handleConfirmClose}
        title="¿Seguro que querés cancelar?"
        message="Se borrarán los datos cargados hasta el momento."
        confirmText="Aceptar"
        variant="warning"
      />
    </>
  );
};

export default ProductionModal;